import { React, useState } from 'react';

import { InputLabel, MenuItem, Select } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { distractCategories } from '../../utils/distractCategories';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/undoDistrato.css';

const DesfazerDistrato = () => {
    const [isCliente, setIsCliente] = useState(true);
    const [cliente, setCliente] = useState('');
    const [clienteId, setClienteId] = useState('');
    const [motivo, setMotivo] = useState('');
    const [unidades, setUnidades] = useState([]);
    const [listaUnidades, setListaUnidades] = useState([]);
    const [notificar, setNotificar] = useState(false);
    const [dadosCliente, setDadosCliente] = useState(null);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [isBuscandoCliente, setIsBuscandoCliente] = useState(false);

    const resetDependents = () => {
        setCliente('');
        setDadosCliente(null);
        setListaUnidades([]);
        setUnidades([]);
    };

    const formatUnidadesCliente = (dados) => {
        const unidadesCliente = dados.unidades || [];
        if (!Array.isArray(unidadesCliente)) {
            return Object.entries(unidadesCliente).map(([unidade, ccs]) => {
                const listaCcs = Array.isArray(ccs) ? ccs : [ccs];

                if (listaCcs.filter(Boolean).length === 0) {
                    return unidade;
                }
                return unidade + ' | ' + listaCcs.filter(Boolean).join(' / ');
            });
        }

        const ccsCliente = dados.ccs || dados.cc || [];
        const ccsPorUnidade = dados.ccs_por_unidade || {};

        return unidadesCliente.map((unidade, index) => {
            if (typeof unidade === 'object') {
                const nomeUnidade = unidade.unidade || unidade.nome || '';
                const ccsUnidade = unidade.ccs || unidade.cc || [];
                const listaCcs = Array.isArray(ccsUnidade) ? ccsUnidade : [ccsUnidade];

                if (listaCcs.filter(Boolean).length === 0) {
                    return nomeUnidade;
                }
                return nomeUnidade + ' | ' + listaCcs.filter(Boolean).join(' / ');
            }

            const ccsUnidade = ccsPorUnidade[unidade] || ccsCliente[index] || ccsCliente;
            const listaCcs = Array.isArray(ccsUnidade) ? ccsUnidade : [ccsUnidade];

            if (listaCcs.filter(Boolean).length === 0) {
                return unidade;
            }
            return unidade + ' | ' + listaCcs.filter(Boolean).join(' / ');
        });
    };

    async function getClienteDistrato () {
        if (!clienteId) {
            resetDependents();
            return;
        }

        setIsBuscandoCliente(true);
        const body = JSON.stringify({
            clientID: clienteId
        });

        const response = await fetchFlask('getClienteDistrato', body);

        if (response.status !== 'error') {
            const dados = response.dados || {};
            const unidadesCliente = formatUnidadesCliente(dados);

            setDadosCliente(dados);
            setCliente(dados.cliente || '');
            setListaUnidades(['Selecionar Todas'].concat(unidadesCliente));
            setUnidades([]);
        } else {
            resetDependents();
            handleAlerta(response);
        }

        setIsBuscandoCliente(false);
    }

    const selectUnidades = (e, values) => {
        if (values.includes('Selecionar Todas')) {
            let todasUnidades = [];
            todasUnidades = todasUnidades.concat(listaUnidades);
            todasUnidades.splice(0, 1);
            setUnidades(todasUnidades);
        } else {
            setUnidades(values);
        }
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    async function submitDesfazerDistrato (e) {
        e.preventDefault();
        setIsSubmit(true);

        if (!dadosCliente) {
            handleAlerta({
                message: 'Busque um cliente antes de desfazer o distrato!',
                status: 'error'
            });
            setIsSubmit(false);
            return;
        }

        let unidadesFetch;
        if (isCliente) {
            unidadesFetch = Array.isArray(dadosCliente.unidades)
                ? dadosCliente.unidades
                : Object.keys(dadosCliente.unidades || {});
        } else {
            unidadesFetch = unidades.map(u => u.split(' | ')[0]);
        }

        const body = JSON.stringify({
            motivo,
            unidades: unidadesFetch,
            notificar
        });
        const response = await fetchFlask('desfazerDistrato', body);
        handleAlerta(response);
        setIsSubmit(false);
        if (response.status === 'success') {
            setClienteId('');
            setCliente('');
            setMotivo('');
            setUnidades([]);
            setDadosCliente(null);
            setListaUnidades([]);
            setNotificar(false);
        }

        setIsSubmit(false);
    };

    const selectedScopeLabel = isCliente ? 'Cliente completo' : 'Unidades específicas';
    const selectedClientLabel = cliente || 'Não carregado';
    const selectedReasonLabel = motivo || 'Não informado';
    const selectedUnitsLabel = isCliente
        ? (dadosCliente ? 'Todas as unidades do cliente' : 'Aguardando cliente')
        : `${unidades.length} selecionada${unidades.length === 1 ? '' : 's'}`;
    const notifyLabel = notificar ? 'Notificar cliente' : 'Não notificar';
    const completedFields = [
        dadosCliente,
        motivo,
        isCliente || unidades.length > 0
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Desfazer Distrato
            </PageHeader>

            <form action='' method='POST' id='formDesfazerDistrato' onSubmit={submitDesfazerDistrato} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Reativação comercial com escopo revisado</h2>
                        </Box>
                        <p>
                            Defina o motivo econfirme se vale para todo o cadastro ou apenas unidades específicas.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <CancelPresentationRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Escopo da reversão</strong>
                            </Box>
                        </Box>

                        <Box className='undo-distrato-mode-row'>
                            <Box>
                                <strong>{selectedScopeLabel}</strong>
                                <span>{isCliente ? 'Reverte o distrato para todas as unidades do cliente.' : 'Reverte somente as unidades selecionadas.'}</span>
                            </Box>
                            <FormControlLabel
                                label={isCliente ? 'Cliente' : 'Unidades'}
                                control={
                                    <Switch
                                        checked={isCliente}
                                        onChange={(e) => {
                                            setIsCliente(e.target.checked);
                                            setUnidades([]);
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <UndoRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Motivo da reversão</strong>
                            </Box>
                        </Box>

                        <FormControl className='ui-control' required>
                            <InputLabel id='labelMotivo'>Motivo</InputLabel>
                            <Select
                                id='selectMotivo'
                                labelId='labelMotivo'
                                label='Motivo'
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                required
                            >
                                {distractCategories.map((category) => (
                                    <MenuItem key={category.value} value={category.value}>
                                        {category.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ManageAccountsRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Cliente e unidades</strong>
                            </Box>
                        </Box>

                        <Box className='ui-lookup-grid'>
                            <TextField
                                required
                                label='Matrícula / Cliente ID'
                                value={clienteId}
                                onChange={(e) => {
                                    setClienteId(e.target.value);
                                    resetDependents();
                                }}
                            />
                            <Button
                                type='button'
                                variant='contained'
                                onClick={getClienteDistrato}
                                disabled={isBuscandoCliente}
                                startIcon={<PersonSearchRoundedIcon />}
                            >
                                {isBuscandoCliente ? 'Buscando...' : 'Buscar Cliente'}
                            </Button>
                        </Box>

                        <Box className='undo-distrato-client-strip'>
                            <DomainRoundedIcon />
                            <Box>
                                <span>Cliente carregado</span>
                                <strong>{selectedClientLabel}</strong>
                            </Box>
                        </Box>

                        {!isCliente && (
                            <Autocomplete
                                className='undo-distrato-units-field'
                                multiple
                                disableCloseOnSelect
                                id='unidades'
                                limitTags={8}
                                value={unidades}
                                options={listaUnidades}
                                onChange={selectUnidades}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Unidades'
                                        required={unidades.length === 0}
                                    />
                                )}
                            />
                        )}
                    </Box>

                    <Box className='undo-distrato-notify-row'>
                        <Box>
                            <NotificationsActiveRoundedIcon />
                            <Box>
                                <span>Comunicação</span>
                                <strong>{notifyLabel}</strong>
                            </Box>
                        </Box>
                        <FormControlLabel
                            label='Notificar'
                            control={
                                <Checkbox
                                    checked={notificar}
                                    onChange={(e) => setNotificar(e.target.checked)}
                                />
                            }
                        />
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da reversão</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Escopo</span>
                            <strong>{selectedScopeLabel}</strong>
                        </Box>
                        <Box>
                            <span>Motivo</span>
                            <strong className={!motivo ? 'is-empty' : undefined}>{selectedReasonLabel}</strong>
                        </Box>
                        <Box>
                            <span>Cliente</span>
                            <strong className={!cliente ? 'is-empty' : undefined}>{selectedClientLabel}</strong>
                        </Box>
                        <Box>
                            <span>{isCliente ? 'Unidades impactadas' : 'Unidades selecionadas'}</span>
                            <strong className={!dadosCliente && isCliente ? 'is-empty' : undefined}>{selectedUnitsLabel}</strong>
                        </Box>
                        <Box>
                            <span>Notificação</span>
                            <strong>{notifyLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <WarningAmberRoundedIcon />
                        <p>
                            A reversão reativa o vínculo comercial do cliente ou das unidades selecionadas. Revise o escopo antes de enviar.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/3 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<BadgeRoundedIcon />}>
                        Desfazer Distrato
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default DesfazerDistrato;
